import type { ConversationThreadId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useQuery } from "@tanstack/react-query";
import { catchApiErrors } from "../db/error-handling";
import { draftKeys } from "./use-draft";

/**
 * Hook to fetch all drafts for a conversation thread.
 * React Query deduplicates — multiple components with the same threadId share one API call.
 */
export function useThreadDrafts(threadId: ConversationThreadId) {
  const effectApi = useRequiredContext(EffectApiClientContext);

  return useQuery({
    queryKey: draftKeys.list({ conversationId: threadId }),
    queryFn: async () => {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrors(
          client.conversation.getThreadDrafts({ path: { id: threadId } }),
        ),
      );

      return result.drafts;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
